/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils';

export const badgeData = {
    PASS: {
        label: 'Passed',
        icon: (
            <svg className="text-darkGreen" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24">
                <path
                    fill="currentColor"
                    d="m18.02 4.76 2.5 2.5-9.93 9.94c-.69.7-1.81.7-2.5 0l-3.95-3.94L2.88 12 5.4 9.49l3.95 3.94 7.42-7.42 1.26-1.25Z"
                />
            </svg>
        ),
        badge: 'bg-green',
        pill: 'border-green text-lime-800',
    },
    FAIL: {
        label: 'Failed',
        icon: (
            <svg
                className="text-[hsla(336,100%,94%,1)]"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
            >
                <path fill="currentColor" d="M17.85 10.22a1.78 1.78 0 0 1 0 3.56H6.8a1.78 1.78 0 1 1 0-3.56h11.05Z" />
            </svg>
        ),
        badge: 'bg-danger',
        pill: 'border-danger text-pink-800',
    },
    OTHER: {
        label: 'Other',
        icon: (
            <svg className="text-slate-400" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24">
                <path
                    fill="currentColor"
                    d="M11.94 5.48c4.1 0 7.08 2.09 8.81 6.13a1 1 0 0 1 0 .78c-1.73 4.04-4.71 6.13-8.8 6.13-4.1 0-7.08-2.09-8.81-6.13a1 1 0 0 1 0-.78c1.73-4.04 4.71-6.13 8.8-6.13Zm0 2c-3.07 0-5.26 1.4-6.7 4.33l-.09.19.1.2c1.38 2.82 3.48 4.23 6.4 4.32h.3c3.07 0 5.26-1.4 6.7-4.33l.09-.2-.1-.18c-1.38-2.83-3.48-4.24-6.4-4.33h-.3Zm0 .97a3.55 3.55 0 1 1 0 7.1 3.55 3.55 0 0 1 0-7.1Zm0 2.37a1.18 1.18 0 1 0 0 2.36 1.18 1.18 0 0 0 0-2.36Z"
                />
            </svg>
        ),
        badge: 'bg-slate-200',
        pill: 'border-slate-200 text-slate-500',
    },
};

const statusLabels = {
    draft: 'In Draft',
    disabled: 'Disabled',
    review: 'In Review',
    maintenance: 'In Repair',
    dismissed: 'Dismissed',
    running: 'Running',
};

interface ExecutionStatusBadgeProps {
    status: 'PASS' | 'FAIL' | 'OTHER';
    number: { count: number; detail?: Record<string, number> };
    compact?: boolean;
    onClick?: () => void;
}

export const ExecutionStatusBadge = ({ status, number, compact, onClick = () => {} }: ExecutionStatusBadgeProps) => {
    if (!status) return null;

    const data = badgeData[status];
    const badge = (
        <button
            onClick={onClick}
            disabled={number.count === 0}
            className={cn('flex outline-0 items-center gap-[12px]', number.count === 0 && 'opacity-10')}
        >
            <div className="flex items-center group">
                <div
                    className={cn(
                        data.badge,
                        'h-[28px] aspect-[1] flex items-center justify-center transition-transform group-hover:scale-[1.05] origin-center rounded-l',
                    )}
                >
                    {data.icon}
                </div>
                <div
                    className={cn(
                        'flex items-center justify-center [text-box:trim-both] whitespace-nowrap leading-[28px] font-[400] text-body h-[28px] rounded-r border border-inset border-[2px] border-l-0',
                        !compact && 'px-[8px]',
                        compact && 'w-[24px]',
                        data.pill,
                    )}
                >
                    <div>
                        <span className="font-bold">{number.count}</span>
                        {!compact && (
                            <>
                                <span> </span>
                                <span>{data.label}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </button>
    );

    if (status === 'OTHER' && number.detail) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{badge}</TooltipTrigger>
                    <TooltipContent side="bottom">
                        <div className="flex flex-col gap-1">
                            {Object.entries(number.detail).map(([key, value]) => (
                                <div key={key} className="flex justify-between gap-4">
                                    <span>{statusLabels[key as keyof typeof statusLabels]}:</span>
                                    <span className="font-bold">{value}</span>
                                </div>
                            ))}
                        </div>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return badge;
};
